import type { OutpostEvent } from './types';

export interface ParsedDsn {
  scheme: string;
  publicKey: string;
  host: string;
  port?: number;
  projectId: string;
  /** Endpoint a single event is POSTed to. */
  eventUrl: string;
  /** Endpoint an NDJSON batch is POSTed to. */
  batchUrl: string;
}

/**
 * Parse a DSN.
 *
 * Throws rather than returning null: a malformed DSN means the application
 * believes it is reporting errors when it is not, and that silence is worse
 * than a startup failure the developer will notice immediately.
 */
export function parseDsn(dsn: string): ParsedDsn {
  let url: URL;
  try {
    url = new URL(dsn);
  } catch {
    throw new Error(`Outpost: malformed DSN "${dsn}"`);
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new Error(`Outpost: DSN scheme must be http or https, got "${url.protocol}"`);
  }
  if (!url.username) {
    throw new Error('Outpost: DSN is missing its public key');
  }
  if (url.password) {
    throw new Error('Outpost: DSNs must not contain a password');
  }

  const projectId = url.pathname.replace(/^\/+|\/+$/g, '');
  if (!projectId) {
    throw new Error('Outpost: DSN is missing its project id');
  }

  const scheme = url.protocol.replace(':', '');
  const port = url.port ? Number(url.port) : undefined;
  const origin = port ? `${scheme}://${url.hostname}:${port}` : `${scheme}://${url.hostname}`;

  return {
    scheme,
    publicKey: url.username,
    host: url.hostname,
    port,
    projectId,
    eventUrl: `${origin}/v1/ingest/${projectId}/events`,
    batchUrl: `${origin}/v1/ingest/${projectId}/batch`,
  };
}

/** Value for the `X-Outpost-Auth` header. */
export function authHeader(dsn: ParsedDsn, clientName: string, version: string): string {
  return `key=${dsn.publicKey}, v=1, client=${clientName}/${version}`;
}

/** True when the event carries anything the server could render. */
export function isReportable(event: OutpostEvent): boolean {
  return Boolean(event.exception?.length || event.message?.trim());
}
